import { motion, useMotionValue, useReducedMotion, useScroll, useSpring, useTransform } from "framer-motion";
import { ChevronDown, MapPin, MessageCircle, Phone } from "lucide-react";
import { BRAND, IMAGES } from "@/content/site";
import { waHref } from "@/content/media";
import { useLang } from "@/lang";
import { scrollToId } from "@/lib/lenis";

const EASE = [0.22, 1, 0.36, 1];

function CrestMedallion() {
  const rm = useReducedMotion();
  const mx = useMotionValue(0);
  const my = useMotionValue(0);
  const rx = useSpring(my, { stiffness: 120, damping: 16 });
  const ry = useSpring(mx, { stiffness: 120, damping: 16 });

  const onTilt = (e) => {
    const r = e.currentTarget.getBoundingClientRect();
    mx.set(((e.clientX - r.left) / r.width - 0.5) * 16);
    my.set(-((e.clientY - r.top) / r.height - 0.5) * 16);
  };
  const offTilt = () => {
    mx.set(0);
    my.set(0);
  };

  return (
    <motion.div
      initial={{ opacity: 0, scale: 0.85 }}
      animate={{ opacity: 1, scale: 1 }}
      transition={{ duration: 1.2, ease: EASE, delay: 0.9 }}
      className="absolute right-20 top-28 hidden xl:block"
    >
      <motion.div
        onMouseMove={onTilt}
        onMouseLeave={offTilt}
        style={{ transformPerspective: 700, rotateX: rm ? 0 : rx, rotateY: rm ? 0 : ry }}
      >
        <motion.img
          src="/logo-crest.png"
          alt="Equipment Concierge EC crest with horse"
          animate={rm ? undefined : { y: [0, -10, 0] }}
          transition={{ duration: 6, repeat: Infinity, ease: "easeInOut" }}
          className="h-44 w-44 rounded-full object-cover shadow-[0_30px_60px_rgba(0,0,0,0.5)] ring-1 ring-gold/60"
        />
      </motion.div>
    </motion.div>
  );
}

export default function Hero() {
  const { t, isEs } = useLang();
  const rm = useReducedMotion();
  const { scrollY } = useScroll();
  const yBg = useTransform(scrollY, [0, 900], [0, 180]);
  const yText = useTransform(scrollY, [0, 900], [0, 60]);
  const fadeMeta = useTransform(scrollY, [0, 500], [1, 0.2]);

  const line = (i) => ({
    hidden: { y: rm ? "0%" : "112%" },
    show: {
      y: "0%",
      transition: { duration: 1.1, ease: EASE, delay: 0.25 + i * 0.14 },
    },
  });
  const fade = (d) => ({
    hidden: { opacity: 0, y: rm ? 0 : 26 },
    show: { opacity: 1, y: 0, transition: { duration: 0.9, ease: EASE, delay: d } },
  });

  return (
    <section id="top" data-testid="hero-section" className="relative flex min-h-[100svh] items-center overflow-hidden bg-navy">
      <motion.div style={rm ? undefined : { y: yBg }} className="absolute inset-0">
        <img
          src={IMAGES.hero.src}
          alt={IMAGES.hero.alt[isEs ? "es" : "en"]}
          fetchpriority="high"
          className={`h-[115%] w-full object-cover ${rm ? "" : "kenburns"}`}
        />
      </motion.div>
      <div className="absolute inset-0 bg-gradient-to-r from-navy/90 via-navy/55 to-navy/30" />
      <div className="absolute inset-0 bg-gradient-to-b from-navy/70 via-transparent to-navy" />

      <CrestMedallion />

      <motion.div style={rm ? undefined : { opacity: fadeMeta }} className="absolute right-8 top-1/2 hidden -translate-y-1/2 lg:block">
        <p className="rotate-90 whitespace-nowrap text-[10px] uppercase tracking-[0.5em] text-cream/50">
          Wellington — Palm Beach County
        </p>
      </motion.div>

      <motion.div style={rm ? undefined : { y: yText }} className="relative z-10 mx-auto w-full max-w-7xl px-4 pb-24 pt-28 sm:px-6 lg:px-8">
        <div className="max-w-3xl">
          <motion.div variants={fade(0.1)} initial="hidden" animate="show" className="mb-7 flex items-center gap-4">
            <span className="h-px w-12 bg-gold" />
            <p className="text-[11px] font-semibold uppercase tracking-[0.35em] text-gold sm:text-xs">
              {t.hero.eyebrow}
            </p>
          </motion.div>

          <h1 className="font-serif text-[clamp(2.6rem,7.5vw,4.9rem)] font-medium leading-[1.04] text-cream">
            {[t.hero.title1, t.hero.title2].map((l, i) => (
              <span key={i} className="block overflow-hidden pb-2">
                <motion.span
                  variants={line(i)}
                  initial="hidden"
                  animate="show"
                  className={`block ${i === 1 ? "italic text-gold" : ""}`}
                >
                  {l}
                </motion.span>
              </span>
            ))}
          </h1>

          <motion.p variants={fade(0.75)} initial="hidden" animate="show" className="mt-6 max-w-xl text-base leading-relaxed text-cream/85 sm:text-lg">
            {t.hero.sub}
          </motion.p>

          <motion.div variants={fade(0.95)} initial="hidden" animate="show" className="mt-9 flex flex-col gap-4 sm:flex-row sm:items-center">
            <a
              href={BRAND.phoneHref}
              data-testid="hero-call-btn"
              className="inline-flex items-center justify-center gap-3 rounded-full bg-gold px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-navy shadow-[0_14px_40px_rgba(201,164,92,0.35)] transition-all duration-300 hover:-translate-y-0.5 hover:bg-gold-hover sm:text-sm"
            >
              <Phone className="h-4 w-4" />
              {t.hero.call}
            </a>
            <a
              href={waHref(isEs ? "es" : "en")}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-btn"
              className="inline-flex items-center justify-center gap-3 rounded-full border border-cream/35 px-8 py-4 text-[13px] font-semibold uppercase tracking-[0.18em] text-cream transition-all duration-300 hover:-translate-y-0.5 hover:border-wa hover:bg-wa hover:text-white sm:text-sm"
            >
              <MessageCircle className="h-4 w-4" />
              {t.hero.wa}
            </a>
          </motion.div>

          <motion.p variants={fade(1.05)} initial="hidden" animate="show" className="mt-5 font-serif text-xl italic text-gold sm:text-2xl">
            {t.nico}
          </motion.p>

          <motion.p variants={fade(1.15)} initial="hidden" animate="show" className="mt-6 flex flex-wrap items-center gap-2 text-[11px] uppercase tracking-[0.25em] text-cream/60">
            <MapPin className="h-3.5 w-3.5 shrink-0 text-gold" />
            {t.hero.trust}
          </motion.p>
        </div>

        <button
          type="button"
          data-testid="hero-scroll-cue"
          onClick={(e) => {
            e.preventDefault();
            scrollToId("problem");
          }}
          aria-label={t.hero.scroll}
          className="absolute bottom-8 left-1/2 z-10 -translate-x-1/2 text-cream/60 transition-colors hover:text-gold"
        >
          <motion.span animate={rm ? undefined : { y: [0, 8, 0] }} transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }} className="flex flex-col items-center gap-1">
            <span className="text-[10px] uppercase tracking-[0.3em]">{t.hero.scroll}</span>
            <ChevronDown className="h-4 w-4" />
          </motion.span>
        </button>
      </motion.div>
    </section>
  );
}
